import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import VpnKeyRoundedIcon from '@mui/icons-material/VpnKeyRounded';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Divider,
  Typography,
  Stack,
  MenuItem,
  Avatar,
  IconButton,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Button,
  TextField,
  Alert,
  InputAdornment,
  Grid,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import account from "../../../_mock/account";
import { logout } from "../../../actions/auth";
import authService from "../../../services/auth.service";
import UserDataService from "../../../pages/services/UserService";
import { useThemeMode } from "../../../theme/ThemeContext";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";
import Switch from "@mui/material/Switch";
import { useTheme, alpha } from "@mui/material/styles";

const MENU_OPTIONS = [
  { label: "Home", icon: HomeRoundedIcon },
  { label: "Profile", icon: PersonOutlineRoundedIcon },
  { label: "Change Password", icon: VpnKeyRoundedIcon },
  { label: "Settings", icon: SettingsOutlinedIcon },
];

const generateStrongPassword = (length = 16) => {
  const uppers = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lowers = "abcdefghijkmnopqrstuvwxyz";
  const numbers = "23456789";
  const symbols = "!@#$%^&*()_+~|}{[]:;?><=";
  const all = uppers + lowers + numbers + symbols;

  const getRandom = (charset) => {
    if (typeof window !== "undefined" && window.crypto && window.crypto.getRandomValues) {
      const arr = new Uint32Array(1);
      window.crypto.getRandomValues(arr);
      return charset[arr[0] % charset.length];
    }
    return charset[Math.floor(Math.random() * charset.length)];
  };

  const passwordChars = [
    getRandom(uppers),
    getRandom(uppers),
    getRandom(lowers),
    getRandom(lowers),
    getRandom(numbers),
    getRandom(numbers),
    getRandom(symbols),
    getRandom(symbols),
  ];

  while (passwordChars.length < length) {
    passwordChars.push(getRandom(all));
  }

  for (let i = passwordChars.length - 1; i > 0; i--) {
    let j;
    if (typeof window !== "undefined" && window.crypto && window.crypto.getRandomValues) {
      const arr = new Uint32Array(1);
      window.crypto.getRandomValues(arr);
      j = arr[0] % (i + 1);
    } else {
      j = Math.floor(Math.random() * (i + 1));
    }
    const temp = passwordChars[i];
    passwordChars[i] = passwordChars[j];
    passwordChars[j] = temp;
  }

  return passwordChars.join("");
};

export default function AccountPopover() {
  const [open, setOpen] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  
  const [cpData, setCpData] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [cpMsg, setCpMsg] = useState({ type: "", text: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [suggestedPassword, setSuggestedPassword] = useState("");
  const [copied, setCopied] = useState(false);
  const [cpLoading, setCpLoading] = useState(false);

  const handleClickShowPassword = () => setShowPassword((s) => !s);
  const handleMouseDownPassword = (e) => e.preventDefault();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user: currentUser } = useSelector((state) => state.auth || {});
  const theme = useTheme();
  const { isDark, toggleTheme } = useThemeMode();

  // Fetch full user (respects simulation view-as user and auto-resolves officer for active simulation role)
  useEffect(() => {
    const fetchUser = async () => {
      try {
        let simUserId = sessionStorage.getItem("mtc_view_as_user");
        const simRole = sessionStorage.getItem("mtc_view_as_role");

        // If simulating a role and no officer ID is set in sessionStorage yet, auto-resolve matching officer
        if (!simUserId && simRole) {
          try {
            const officersRes = await UserDataService.getFinancialOfficers();
            const officersList = officersRes.data || [];
            const match = officersList.find((o) => {
              const rNames = (o.roles || []).map((r) => ((typeof r === "object" && r !== null ? r.name : String(r)) || "").toLowerCase().replace(/^role_/, ""));
              if (simRole === "financial") return rNames.includes("financial") || rNames.includes("financial_1");
              if (simRole === "financial_2") return rNames.includes("financial_2");
              if (simRole === "financial_3") return rNames.includes("financial_3");
              if (simRole === "business") return rNames.includes("business");
              if (simRole === "president") return rNames.includes("president");
              if (simRole === "secretary" || simRole === "registrar") return rNames.includes("registrar") || rNames.includes("secretary");
              return false;
            });
            if (match) {
              simUserId = match._id || match.id;
              sessionStorage.setItem("mtc_view_as_user", simUserId);
              sessionStorage.setItem("mtc_view_as_name", match.firstName ? `${match.firstName} ${match.lastName || ""}`.trim() : match.username);
            }
          } catch (e) {
            console.error("Failed to auto-resolve officer for role:", e);
          }
        }

        const id = simUserId || currentUser?._id || currentUser?.id;
        if (!id) return;

        const res = await UserDataService.get(id);
        setUserData(res.data || {});
      } catch (err) {
        console.error("Failed to fetch user:", err);
      }
    };

    fetchUser();

    const handleViewChange = () => fetchUser();
    window.addEventListener("mtc-view-as-change", handleViewChange);
    return () => window.removeEventListener("mtc-view-as-change", handleViewChange);
  }, [currentUser]);

  // Helper for safe values
  const getVal = (field, fallback = "-") =>
    userData?.[field] ??
    currentUser?.[field] ??
    fallback;

  // Format date
  const formatDate = (date) => {
    if (!date) return "-";
    const d = new Date(date);
    return isNaN(d) ? "-" : d.toLocaleDateString();
  };

  const handleOpen = (event) => setOpen(event.currentTarget);
  const handleClose = () => setOpen(null);

  const handleProfileOpen = () => {
    handleClose();
    setProfileOpen(true);
  };

  const handleProfileClose = () => setProfileOpen(false);

  const handleChangePasswordOpen = () => {
    handleClose();
    setCpMsg({ type: "", text: "" });
    setCpData({ currentPassword: "", newPassword: "", confirmPassword: "" });
    setSuggestedPassword("");
    setCopied(false);
    setChangePasswordOpen(true);
  };

  const handleSuggestPassword = () => {
    const strongPass = generateStrongPassword(16);
    setCpData((prev) => ({
      ...prev,
      newPassword: strongPass,
      confirmPassword: strongPass,
    }));
    setShowPassword(true);
    setSuggestedPassword(strongPass);
    if (typeof navigator !== "undefined" && navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(strongPass).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
    setCpMsg({
      type: "info",
      text: "Suggested 16-character strong password applied and copied to clipboard!",
    });
  };

  const handleCopyPassword = () => {
    if (!suggestedPassword) return;
    if (typeof navigator !== "undefined" && navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(suggestedPassword).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const handleChangePasswordClose = () => setChangePasswordOpen(false);

  const handleSubmitPasswordChange = async (e) => {
    e.preventDefault();
    setCpMsg({ type: "", text: "" });
    if (cpData.newPassword !== cpData.confirmPassword) {
      return setCpMsg({ type: "error", text: "New passwords do not match." });
    }
    if (cpData.newPassword.length < 6) {
      return setCpMsg({ type: "error", text: "New password must be at least 6 characters." });
    }
    
    setCpLoading(true);
    try {
      const res = await authService.changePassword(cpData.currentPassword, cpData.newPassword);
      setCpMsg({ type: "success", text: res.data?.message || "Password changed successfully." });
      setCpData({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to change password.";
      setCpMsg({ type: "error", text: msg });
    } finally {
      setCpLoading(false);
    }
  };

  const handleMenuClick = (label) => {
    if (label === "Profile") return handleProfileOpen();
    if (label === "Change Password") return handleChangePasswordOpen();
    if (label === "Home") navigate("/dashboard");
    if (label === "Settings") { navigate("/dashboard/settings"); handleClose(); return; }
    handleClose();
  };

  const logOut = useCallback(() => {
    dispatch(logout());
    navigate("/landing");
  }, [dispatch, navigate]);

  return (
    <>
      {/* Avatar Button */}
      <IconButton
        onClick={handleOpen}
        sx={{
          p: 0,
          border: open ? "2px solid #0B3B60" : "2px solid transparent",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            transform: "scale(1.05)",
          },
        }}
      >
        <Avatar
          src={account.photoURL}
          sx={{
            width: 40,
            height: 40,
            bgcolor: "#0B3B60",
            fontWeight: 700,
            fontSize: "1rem",
            color: "#ffffff",
            boxShadow: "0 2px 8px rgba(11, 59, 96, 0.25)",
          }}
        >
          {getVal("username", "U")?.charAt(0).toUpperCase()}
        </Avatar>
      </IconButton>

      {/* Modern Styled Popover */}
      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            p: 0,
            mt: 1.5,
            ml: 0.75,
            width: 280,
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 20px 40px -10px rgba(11, 59, 96, 0.25), 0 0 0 1px rgba(226, 232, 240, 0.8)",
            border: "1px solid #E2E8F0",
          },
        }}
      >
        {/* User Card Header */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #0B3B60 0%, #072740 100%)",
            color: "#FFFFFF",
            p: 2.25,
            position: "relative",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              src={account.photoURL}
              sx={{
                width: 44,
                height: 44,
                bgcolor: "#059669",
                border: "2px solid #FFFFFF",
                fontWeight: 700,
                fontSize: "1.1rem",
                boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
              }}
            >
              {getVal("username", "U")?.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ overflow: "hidden" }}>
              <Typography variant="subtitle1" noWrap sx={{ fontWeight: 800, color: "#FFFFFF", lineHeight: 1.2 }}>
                {getVal("firstName") !== "-" && getVal("lastName") !== "-"
                  ? `${getVal("firstName")} ${getVal("lastName")}`
                  : getVal("username")}
              </Typography>
              <Typography variant="caption" noWrap sx={{ color: "rgba(255, 255, 255, 0.75)", display: "block" }}>
                {getVal("email")}
              </Typography>
            </Box>
          </Stack>

          {/* Member Status & Reg Badge */}
          <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" gap={0.5} sx={{ mt: 1.5 }}>
            <Chip
              size="small"
              label={getVal("approvedOrNotApproved") !== "-" ? getVal("approvedOrNotApproved") : "Active"}
              sx={{
                height: 20,
                fontSize: "0.68rem",
                fontWeight: 700,
                backgroundColor: "#059669",
                color: "#FFFFFF",
              }}
            />
            {getVal("registrationNo") !== "-" && (
              <Chip
                size="small"
                label={`Reg: ${getVal("registrationNo")}`}
                sx={{
                  height: 20,
                  fontSize: "0.68rem",
                  fontWeight: 600,
                  backgroundColor: "rgba(255,255,255,0.15)",
                  color: "#FFFFFF",
                }}
              />
            )}
            {getVal("membershipId") !== "-" && (
              <Chip
                size="small"
                label={`ID: ${getVal("membershipId")}`}
                sx={{
                  height: 20,
                  fontSize: "0.68rem",
                  fontWeight: 600,
                  backgroundColor: "rgba(255,255,255,0.15)",
                  color: "#FFFFFF",
                }}
              />
            )}
          </Stack>
        </Box>

        {/* Menu Options List */}
        <Stack sx={{ p: 1.25 }}>
          {MENU_OPTIONS.map((opt) => {
            const IconComponent = opt.icon;
            return (
              <MenuItem
                key={opt.label}
                onClick={() => handleMenuClick(opt.label)}
                sx={{
                  borderRadius: "10px",
                  py: 1.1,
                  px: 1.5,
                  mb: 0.4,
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: "#334155",
                  transition: "all 0.15s ease-in-out",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  "&:hover": {
                    backgroundColor: "#F1F5F9",
                    color: "#0B3B60",
                    transform: "translateX(4px)",
                  },
                }}
              >
                <IconComponent sx={{ fontSize: "1.2rem", color: "#64748B" }} />
                <span>{opt.label}</span>
              </MenuItem>
            );
          })}
        </Stack>

        {/* Quick Theme Switcher */}
        <Box sx={{ px: 2, py: 1.25, bgcolor: alpha(theme.palette.grey[500], 0.08), borderRadius: '10px', mx: 1.5, my: 0.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1}>
              {isDark ? (
                <DarkModeRoundedIcon sx={{ fontSize: 18, color: '#38BDF8' }} />
              ) : (
                <LightModeRoundedIcon sx={{ fontSize: 18, color: '#F59E0B' }} />
              )}
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                {isDark ? 'Dark Mode' : 'Light Mode'}
              </Typography>
            </Stack>
            <Switch
              size="small"
              checked={isDark}
              onChange={toggleTheme}
            />
          </Stack>
        </Box>

        <Divider sx={{ borderStyle: "dashed", my: 0.5 }} />

        {/* Logout Button */}
        <Box sx={{ p: 1.25, pt: 0.5 }}>
          <MenuItem
            onClick={logOut}
            sx={{
              borderRadius: "10px",
              py: 1.1,
              px: 1.5,
              fontSize: "0.875rem",
              fontWeight: 700,
              color: "#DC2626",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              transition: "all 0.15s ease-in-out",
              "&:hover": {
                backgroundColor: "rgba(239, 68, 68, 0.08)",
                color: "#B91C1C",
                transform: "translateX(4px)",
              },
            }}
          >
            <LogoutRoundedIcon sx={{ fontSize: "1.2rem", color: "#DC2626" }} />
            <span>Logout</span>
          </MenuItem>
        </Box>
      </Popover>

      {/* Profile Dialog */}
      <Dialog
        open={profileOpen}
        onClose={handleProfileClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 24px 48px -12px rgba(11, 59, 96, 0.25)",
            border: "1px solid #E2E8F0",
          },
        }}
      >
        {/* Modern Navy Header Banner */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #0B3B60 0%, #072740 100%)",
            color: "#FFFFFF",
            p: { xs: 2.5, sm: 3.5 },
            position: "relative",
          }}
        >
          <IconButton
            onClick={handleProfileClose}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: "rgba(255, 255, 255, 0.8)",
              "&:hover": { color: "#FFFFFF", backgroundColor: "rgba(255, 255, 255, 0.1)" },
            }}
          >
            ✕
          </IconButton>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5} alignItems={{ xs: "flex-start", sm: "center" }}>
            <Avatar
              src={account.photoURL}
              sx={{
                width: { xs: 64, sm: 80 },
                height: { xs: 64, sm: 80 },
                fontSize: "2rem",
                fontWeight: 700,
                bgcolor: "#059669",
                border: "3px solid #FFFFFF",
                boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
              }}
            >
              {getVal("username", "U")?.charAt(0).toUpperCase()}
            </Avatar>

            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#FFFFFF", letterSpacing: "-0.01em" }}>
                  {getVal("firstName") !== "-" && getVal("lastName") !== "-"
                    ? `${getVal("firstName")} ${getVal("middleName") !== "-" ? getVal("middleName") + " " : ""}${getVal("lastName")}`
                    : getVal("username")}
                </Typography>
                {(() => {
                  const raw = getVal("approvedOrNotApproved");
                  const val = raw !== "-" ? raw : "Active";
                  const lower = val.toLowerCase();
                  const isAct = lower === "active" || lower === "approved";
                  const isNon = lower === "non-active" || lower === "inactive" || lower === "non active";
                  const isWdr = lower === "withdraw" || lower === "withdrawn" || lower === "withdrawal";
                  const isExp = lower === "expel" || lower === "expelled";
                  const isDec = lower === "decease" || lower === "deceased";
                  const label = isExp ? "Expel" : isDec ? "Decease" : isWdr ? "Withdraw" : isNon ? "Non-Active" : "Active";
                  const bg = isAct ? "#059669" : isNon ? "#D97706" : isWdr ? "#854D0E" : isExp ? "#DC2626" : isDec ? "#475569" : "#059669";
                  return (
                    <Chip
                      size="small"
                      label={label}
                      sx={{
                        fontWeight: 700,
                        backgroundColor: bg,
                        color: "#FFFFFF",
                        fontSize: "0.75rem",
                      }}
                    />
                  );
                })()}
              </Stack>

              <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.8)", mb: 1 }}>
                {getVal("email")} {getVal("phoneNumber") !== "-" ? `• ${getVal("phoneNumber")}` : ""}
              </Typography>

              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                <Chip
                  size="small"
                  label={`Reg No: ${getVal("registrationNo")}`}
                  sx={{
                    bgcolor: "rgba(255, 255, 255, 0.15)",
                    color: "#FFFFFF",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    border: "1px solid rgba(255, 255, 255, 0.25)",
                  }}
                />
                {getVal("membershipId") !== "-" && (
                  <Chip
                    size="small"
                    label={`Member ID: ${getVal("membershipId")}`}
                    sx={{
                      bgcolor: "rgba(255, 255, 255, 0.15)",
                      color: "#FFFFFF",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      border: "1px solid rgba(255, 255, 255, 0.25)",
                    }}
                  />
                )}
                {/* Roles badge */}
                {(userData?.roles || currentUser?.roles || []).map((r, i) => {
                  const roleName = typeof r === "object" ? (r.name || "") : String(r);
                  const cleanKey = String(roleName || "").toLowerCase().replace(/^role_/, "");
                  const labels = {
                    admin: "Admin",
                    financial: "Financial I",
                    financial_1: "Financial I",
                    financial_2: "Financial II",
                    financial_3: "Financial III",
                    business: "Business",
                    president: "President",
                    registrar: "Secretary",
                    user: "Member",
                  };
                  return (
                    <Chip
                      key={i}
                      size="small"
                      label={labels[cleanKey] || roleName}
                      sx={{
                        bgcolor: "#FFFFFF",
                        color: "#0B3B60",
                        fontWeight: 700,
                        fontSize: "0.75rem",
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>
          </Stack>
        </Box>

        {/* Scrollable Structured Sections */}
        <Box
          sx={{
            p: { xs: 2.5, sm: 3.5 },
            maxHeight: "65vh",
            overflowY: "auto",
            backgroundColor: "#F8FAFC",
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-thumb": { backgroundColor: "#CBD5E1", borderRadius: "4px" },
          }}
        >
          <Stack spacing={3}>
            {/* 1. Personal Information */}
            <Box sx={{ backgroundColor: "#FFFFFF", p: 2.5, borderRadius: 2, border: "1px solid #E2E8F0" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0B3B60", mb: 2, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.78rem" }}>
                Personal Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>First Name</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{getVal("firstName")}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Middle Name</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{getVal("middleName")}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Last Name</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{getVal("lastName")}</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Gender</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{getVal("sex")}</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Marital Status</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{getVal("maritalStatus")}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Date of Birth</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{formatDate(userData?.date || userData?.dob)}</Typography>
                </Grid>
              </Grid>
            </Box>

            {/* 2. Contact & Location */}
            <Box sx={{ backgroundColor: "#FFFFFF", p: 2.5, borderRadius: 2, border: "1px solid #E2E8F0" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0B3B60", mb: 2, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.78rem" }}>
                Contact & Address
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Email Address</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{getVal("email")}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Phone Number</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{getVal("phoneNumber")}</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>State of Origin</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{userData?.state || userData?.stateName || "-"}</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Home Town</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{getVal("homeTown")}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Place of Work</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{getVal("applicantsPlaceOfWork")}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Residential Address</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{getVal("residenceAddress")}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Postal Address</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{getVal("postalAddress")}</Typography>
                </Grid>
              </Grid>
            </Box>

            {/* 3. Referee & Membership Status */}
            <Box sx={{ backgroundColor: "#FFFFFF", p: 2.5, borderRadius: 2, border: "1px solid #E2E8F0" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0B3B60", mb: 2, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.78rem" }}>
                Referee & Cooperative Status
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Referee Name</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{getVal("nameOfReferee")}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Referee Phone</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{getVal("refereePhoneNumber")}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Referee Email</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{getVal("refereeEmail")}</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Applicant Date</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{formatDate(userData?.applicantDate)}</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Membership Status</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{getVal("approvedOrNotApproved") !== "-" ? getVal("approvedOrNotApproved") : "Active"}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>Status Date</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>{formatDate(userData?.statusDate)}</Typography>
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </Box>

        {/* Modal Actions */}
        <Box sx={{ p: 2, px: 3, borderTop: "1px solid #E2E8F0", display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "#FFFFFF" }}>
          <Button
            size="small"
            variant="text"
            color="primary"
            onClick={() => {
              handleProfileClose();
              handleChangePasswordOpen();
            }}
            sx={{ fontWeight: 600, textTransform: "none" }}
          >
            Change Password
          </Button>

          <Button
            variant="contained"
            onClick={handleProfileClose}
            sx={{
              backgroundColor: "#0B3B60",
              "&:hover": { backgroundColor: "#072740" },
              textTransform: "none",
              fontWeight: 700,
              px: 3,
            }}
          >
            Close
          </Button>
        </Box>
      </Dialog>
      
      {/* Change Password Dialog - Mobile Optimized with Sticky Actions */}
      <Dialog
        open={changePasswordOpen}
        onClose={handleChangePasswordClose}
        maxWidth="xs"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: { xs: 2.5, sm: 3 },
            m: { xs: 1.5, sm: 2 },
            maxHeight: { xs: "calc(100vh - 24px)", sm: "calc(100vh - 48px)" },
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            boxShadow: "0 24px 48px -12px rgba(11, 59, 96, 0.35)",
            border: "1px solid #E2E8F0",
          },
        }}
      >
        <form
          onSubmit={handleSubmitPasswordChange}
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            maxHeight: "inherit",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <DialogTitle
            sx={{
              p: { xs: 2, sm: 2.5 },
              pb: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid #F1F5F9",
              flexShrink: 0,
            }}
          >
            <Box>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 800,
                  color: "#0B3B60",
                  fontSize: { xs: "1.05rem", sm: "1.15rem" },
                }}
              >
                Change Account Password
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748B", display: "block", mt: 0.25 }}>
                Enter current password and choose a strong new password.
              </Typography>
            </Box>
            <IconButton onClick={handleChangePasswordClose} size="small" sx={{ color: "#94A3B8", ml: 1 }}>
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          {/* Scrollable Content */}
          <DialogContent
            dividers
            sx={{
              p: { xs: 2, sm: 2.5 },
              overflowY: "auto",
              flex: "1 1 auto",
            }}
          >
            {/* Password Generator Banner */}
            <Box
              sx={{
                p: 1.5,
                mb: 2,
                borderRadius: 2,
                backgroundColor: alpha("#0B3B60", 0.04),
                border: "1px dashed",
                borderColor: alpha("#0B3B60", 0.25),
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Box>
                <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, display: "block" }}>
                  Generator
                </Typography>
                <Typography variant="body2" sx={{ color: "#1E293B", fontWeight: 600, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                  16-Char Strong Password
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AutoFixHighRoundedIcon fontSize="small" />}
                onClick={handleSuggestPassword}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  borderRadius: 1.5,
                  borderColor: "#0B3B60",
                  color: "#0B3B60",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  "&:hover": { borderColor: "#072740", backgroundColor: alpha("#0B3B60", 0.08) },
                }}
              >
                Generate
              </Button>
            </Box>

            {suggestedPassword && (
              <Box
                sx={{
                  p: 1.5,
                  mb: 2,
                  borderRadius: 2,
                  backgroundColor: "#F8FAFC",
                  border: "1px solid #CBD5E1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Box sx={{ overflow: "hidden" }}>
                  <Typography variant="caption" sx={{ color: "#64748B", display: "block", fontWeight: 600 }}>
                    Suggested (Copied):
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "monospace",
                      fontWeight: 700,
                      color: "#0B3B60",
                      letterSpacing: 0.5,
                      wordBreak: "break-all",
                      fontSize: { xs: "0.75rem", sm: "0.85rem" },
                    }}
                  >
                    {suggestedPassword}
                  </Typography>
                </Box>
                <Tooltip title={copied ? "Copied to clipboard!" : "Copy to clipboard"}>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={copied ? <CheckRoundedIcon fontSize="small" /> : <ContentCopyRoundedIcon fontSize="small" />}
                    onClick={handleCopyPassword}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      color: copied ? "#10B981" : "#0B3B60",
                      flexShrink: 0,
                    }}
                  >
                    {copied ? "Copied" : "Copy"}
                  </Button>
                </Tooltip>
              </Box>
            )}

            {cpMsg.text && (
              <Alert severity={cpMsg.type} sx={{ mb: 2, borderRadius: 2 }}>
                {cpMsg.text}
              </Alert>
            )}

            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <TextField
                label="Current Password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                required
                fullWidth
                size="small"
                value={cpData.currentPassword}
                onChange={(e) => setCpData((prev) => ({ ...prev, currentPassword: e.target.value }))}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="New Password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                required
                fullWidth
                size="small"
                value={cpData.newPassword}
                onChange={(e) => setCpData((prev) => ({ ...prev, newPassword: e.target.value }))}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Confirm New Password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                required
                fullWidth
                size="small"
                value={cpData.confirmPassword}
                onChange={(e) => setCpData((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </DialogContent>

          {/* Sticky Bottom Actions - Always Visible on Mobile */}
          <DialogActions
            sx={{
              p: { xs: 2, sm: 2.5 },
              pt: 1.5,
              borderTop: "1px solid #E2E8F0",
              backgroundColor: "#FAFAFA",
              display: "flex",
              flexDirection: { xs: "column-reverse", sm: "row" },
              gap: { xs: 1, sm: 1.5 },
              flexShrink: 0,
            }}
          >
            <Button
              onClick={handleChangePasswordClose}
              variant="outlined"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderColor: "#CBD5E1",
                color: "#475569",
                minHeight: 44,
                width: { xs: "100%", sm: "auto" },
                m: "0 !important",
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={cpLoading}
              startIcon={cpLoading ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{
                backgroundColor: "#0B3B60",
                "&:hover": { backgroundColor: "#072740" },
                textTransform: "none",
                fontWeight: 700,
                minHeight: 44,
                px: 3,
                width: { xs: "100%", sm: "auto" },
                boxShadow: "0 4px 12px rgba(11, 59, 96, 0.25)",
                m: "0 !important",
              }}
            >
              {cpLoading ? "Updating..." : "Update Password"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}